import { useQuery } from "@tanstack/react-query";
import { Cpu, KeyRound, ScrollText, Sparkles, ToggleLeft, ToggleRight } from "lucide-react";
import {
  api,
  platformApi,
  type ConfigResponse,
  type LicenseStatus,
  type RuleDescriptor,
} from "@/lib/api";

/**
 * Settings page — read-only runtime introspection.
 *
 * <p>Sections:
 * <ul>
 *   <li><strong>LLM provider</strong> — current LLM (or "fallback" when
 *       none is configured; Atlas's rules are deterministic and don't
 *       require an LLM).</li>
 *   <li><strong>License</strong> — full status pulled from
 *       {@code /api/v1/license/status}, mirrored next to the header
 *       badge so the operator can see the hardware fingerprint at a
 *       glance.</li>
 *   <li><strong>Feature flags</strong> — per-feature on/off so an
 *       operator can diagnose a "why is X greyed out" UI without
 *       shelling into the container.</li>
 *   <li><strong>Governance rules</strong> — registry of every loaded
 *       {@code GovernanceRule} bean. Confirms what's actually
 *       evaluating without grepping the jar.</li>
 * </ul>
 */
export function SettingsPage() {
  const config = useQuery({
    queryKey: ["atlas-config"],
    queryFn: async () => (await platformApi.get<ConfigResponse>("/config")).data,
    staleTime: 60_000,
    retry: false,
  });
  const license = useQuery({
    queryKey: ["license-status"],
    queryFn: async () => (await platformApi.get<LicenseStatus>("/license/status")).data,
    staleTime: 5 * 60_000,
    retry: false,
  });
  const rules = useQuery({
    queryKey: ["atlas-rules"],
    queryFn: async () => (await api.get<RuleDescriptor[]>("/rules")).data,
    staleTime: 60_000,
    retry: false,
  });

  return (
    <div className="mx-auto max-w-3xl space-y-4 p-6">
      <h1 className="font-display text-2xl font-semibold text-atlas-ink">Settings</h1>
      <p className="text-sm text-atlas-slate">
        Read-only summary of how Atlas is running on this host. Use this
        to confirm a deployment is wired correctly before running a
        compliance scan.
      </p>

      <div data-tour-id="settings-llm">
      <Section icon={<Cpu size={14} />} title="LLM provider">
        <Row label="Active provider" value={config.data?.llmProvider ?? "—"} />
        <Row label="Product version" value={config.data?.productVersion ?? "—"} mono />
        <Row
          label="Loaded rules"
          value={config.data?.ruleCount?.toString() ?? "—"}
        />
        {config.data?.llmProvider === "fallback" && (
          <p className="mt-2 rounded-md bg-atlas-mist px-3 py-2 text-2xs text-atlas-slate">
            No LLM configured — Atlas's compliance rules are
            deterministic and don't require an LLM, so this is the
            normal deployment shape. Set <code>CLOUDCRAFT_LLM_PROVIDER</code>
            in your <code>.env</code> if a future Atlas tool needs LLM
            help.
          </p>
        )}
      </Section>
      </div>

      <Section icon={<KeyRound size={14} />} title="License">
        {license.isError && (
          <p className="text-sm text-atlas-slate">
            License gate disabled on this deployment.
          </p>
        )}
        {license.data && (
          <>
            <Row
              label="Status"
              value={license.data.ok ? "Valid" : `Invalid: ${license.data.failureReason ?? "unknown"}`}
              tone={license.data.ok ? "ok" : "bad"}
            />
            {license.data.ok && (
              <>
                <Row label="Issued to" value={license.data.customerName ?? "—"} />
                <Row
                  label="Expires"
                  value={`${formatDate(license.data.expiresAt)} (${license.data.daysRemaining}d)`}
                  tone={license.data.daysRemaining <= 14 ? "warn" : undefined}
                />
                <Row label="Issued" value={formatDate(license.data.issuedAt)} />
              </>
            )}
            {!license.data.ok && license.data.failureMessage && (
              <p className="mt-2 rounded-md bg-atlas-danger/10 px-3 py-2 text-2xs text-atlas-danger">
                {license.data.failureMessage}
              </p>
            )}
            <Row label="Hardware fingerprint" value={license.data.fingerprint} mono />
          </>
        )}
      </Section>

      <Section icon={<Sparkles size={14} />} title="Feature flags">
        {config.data?.featureFlags ? (
          <ul className="space-y-1.5">
            {Object.entries(config.data.featureFlags).map(([flag, on]) => (
              <li key={flag} className="flex items-center justify-between text-sm">
                <span className="text-atlas-ink">{flag}</span>
                <span
                  className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-2xs font-medium ${
                    on
                      ? "bg-atlas-success/10 text-atlas-success"
                      : "bg-atlas-slate/10 text-atlas-slate"
                  }`}
                >
                  {on ? <ToggleRight size={11} /> : <ToggleLeft size={11} />}
                  {on ? "on" : "off"}
                </span>
              </li>
            ))}
          </ul>
        ) : (
          <p className="text-sm text-atlas-slate">Loading feature flags…</p>
        )}
      </Section>

      <div data-tour-id="settings-rules">
      <Section icon={<ScrollText size={14} />} title="Governance rules">
        {rules.isLoading && (
          <p className="text-sm text-atlas-slate">Loading rules…</p>
        )}
        {rules.error && (
          <p className="text-sm text-atlas-danger">
            Couldn't load rules — {(rules.error as Error).message}
          </p>
        )}
        {rules.data && rules.data.length === 0 && (
          <p className="text-sm text-atlas-slate">No rules registered.</p>
        )}
        {rules.data && rules.data.length > 0 && (
          <ul className="space-y-1.5">
            {rules.data.map((rule) => (
              <li
                key={rule.id}
                className="flex items-center justify-between rounded-md bg-atlas-mist/40 px-3 py-2 text-sm"
              >
                <span className="font-mono text-atlas-ink">{rule.id}</span>
                <span className="text-2xs text-atlas-slate">{rule.className}</span>
              </li>
            ))}
          </ul>
        )}
      </Section>
      </div>
    </div>
  );
}

function Section({
  icon,
  title,
  children,
}: {
  icon: React.ReactNode;
  title: string;
  children: React.ReactNode;
}) {
  return (
    <section className="rounded-lg border border-atlas-slate/20 bg-white p-4 shadow-card">
      <h2 className="mb-3 inline-flex items-center gap-2 text-sm font-semibold text-atlas-ink">
        {icon}
        {title}
      </h2>
      {children}
    </section>
  );
}

function Row({
  label,
  value,
  mono,
  tone,
}: {
  label: string;
  value: string;
  mono?: boolean;
  tone?: "ok" | "warn" | "bad";
}) {
  const valueClass =
    tone === "ok"
      ? "text-atlas-success"
      : tone === "warn"
      ? "text-atlas-warn"
      : tone === "bad"
      ? "text-atlas-danger"
      : "text-atlas-ink";
  return (
    <div className="grid grid-cols-[auto,1fr] gap-x-3 gap-y-1 py-1 text-sm">
      <dt className="text-atlas-slate">{label}</dt>
      <dd
        className={`text-right font-medium ${valueClass} ${
          mono ? "break-all font-mono text-2xs" : ""
        }`}
      >
        {value}
      </dd>
    </div>
  );
}

function formatDate(iso?: string | null): string {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleDateString(undefined, {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  } catch {
    return iso;
  }
}
